import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@shadcn/components/ui/badge';
import { Checkbox } from '@shadcn/components/ui/checkbox';
import SKUToolTip from '../../pages/JIFI/helpers/SKUToolTip.js';
import { fulfillmentToBadge, pickToName, pickToBadge, priorityColor } from './UnfulfilledWarehouseOrders.js';

// Regular orders columns
export const getRegularOrdersColumns = ({
  linkBasePath,
  findSKULevel,
  findUpcomingStock,
  findSKUStatus,
  columnHelper,
  // Selection props
  selectAll = false,
  selected = [],
  onSelectAll = () => {},
  onSelectOrder = () => {}
}) => [
  // Selection checkbox column
  columnHelper.accessor('select', {
    header: () => (
      <Checkbox
        checked={selectAll}
        onCheckedChange={onSelectAll}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={selected.includes(row.original._id)}
        onCheckedChange={() => onSelectOrder(row.original._id)}
      />
    ),
    size: 50,
  }),
  columnHelper.accessor('name', {
    header: () => 'Order ID',
    cell: ({ row: { original } }) => {
      return <div className="text-[13px] min-w-max !px-4">
        <Link to={`${linkBasePath}/${original['_id']}`} className="text-body font-weight-bold">{original.name}</Link>
      </div>;
    },
  }),

  columnHelper.accessor('pick_status', {
    header: () => (<div className='min-w-max'>Pick Status</div>),
    cell: ({ row: { original } }) => (
      <Badge
        className={`!px-4 min-w-max text-[12px] badge-soft-${pickToBadge(original.pick_status)}`}
        color={pickToBadge(original.pick_status)}
      >
        {pickToName(original.pick_status)}
      </Badge>
    ),
  }),

  columnHelper.accessor('fulfillment_status', {
    header: () => 'Fulfillment',
    cell: ({ row: { original } }) => {
      return (
        <Badge
          className={`!px-4 text-[12px] badge-soft-${fulfillmentToBadge(
            original.fulfillment_status
          )}`}
        >
          {original.fulfillment_status
            ? original.fulfillment_status.replace('_', ' ').toUpperCase()
            : 'UN-FULFILLED'}
        </Badge>
      );
    },
  }),

  columnHelper.accessor('order_source', {
    header: () => 'Source',
    cell: ({ row: { original } }) => {
      return <p className='!px-4 text-[13px]'>{original.order_source?? ""}</p>;
    },
  }),
  
  columnHelper.accessor('shipping_type', {
    header: () => 'Shipping',
    cell: ({ row: { original } }) => {
      return <p className='!px-4 text-[13px]'>{original.shipping_type } </p>;
    },
  }),
  
  columnHelper.accessor('shipping_address', {
    header: () => <div className="whitespace-nowrap min-w-max">Region</div>,
    cell: ({ row: { original } }) => {
      const { province, country } = original.shipping_address || {};
      return (
        <div className="whitespace-nowrap min-w-max !px-4 text-[13px]">
          {province && country ? `${province}, ${country}` : ''}
        </div>
      );
    },
  }),

  columnHelper.accessor('shipping_address.name', {
    header: () => <div className="whitespace-nowrap min-w-[120px]">Billing Name</div>,
    cell: ({ row: { original } }) => {
      return (
        <div className="whitespace-nowrap min-w-[120px] !px-4 text-[13px]">
          {original.shipping_address?.name || ''}
        </div>
      );
    },
  }),

  columnHelper.accessor('orderCreated', {
    header: () => 'Date',
    cell: ({ row: { original } }) => {
      return (
        <div className="whitespace-nowrap min-w-[100px] text-[13px]">
          {original.orderCreated ? new Date(original.orderCreated).toDateString() : ""}
        </div>
      );
    },
  }),

  columnHelper.accessor('skus', {
    header: () => 'SKUs',
    cell: ({ row: { original }, row }) => {
      if (original.lineSum?.length === 1) {
        return (
          <div className="min-w-[120px]">
            <Badge
              key={original.lineSum[0].sku}
              className="font-size-12 badge-soft-primary"
              variant="primary"
            >
              <SKUToolTip sku={original.lineSum[0].sku} findSKULevel={findSKULevel} findUpcomingStock={findUpcomingStock} findSKUStatus={findSKUStatus} />
            </Badge>
          </div>
        );
      }
                        let firstSKU = original?.lineSum?.[0]?.sku || '';
       let firstQuantity = original?.lineSum?.[0]?.quantity || '';
       
       return (
        <div className="flex items-center min-w-[120px]">
          <Badge
            key={firstSKU}
            className="badge-soft-primary font-size-12"
            variant="primary"
          >
            <SKUToolTip sku={firstSKU} findSKULevel={findSKULevel} findUpcomingStock={findUpcomingStock} findSKUStatus={findSKUStatus} /> x {firstQuantity}
          </Badge>
          <button
            onClick={() => row.toggleExpanded()}
            aria-label="expand row"
            className={`ml-2 w-4 h-4 flex items-center justify-center rounded-full text-white ${
              row.getIsExpanded() ? 'bg-blue-500' : 'bg-gray-500'
            }`}
          >
            {row.getIsExpanded() ? '-' : '+'}
          </button>
        </div>
      );
    },
  }),

  columnHelper.accessor('total_price', {
    header: () => <div className="whitespace-nowrap min-w-[80px]">Total</div>,
    cell: ({ row: { original } }) => {
      return (
        <div className="whitespace-nowrap min-w-[80px] !px-4 text-[13px]">
          {original.total_price ? `$${original.total_price}` : '$0.00'}
        </div>
      );
    },
  }),

  columnHelper.accessor('financial_status', {
    header: () => 'Payment',
    cell: ({ row: { original } }) => {
      return (
        <Badge
          className={`!px-4 text-[12px] badge-soft-${original.badgeClass || 'secondary'}`}
        >
          {original.financial_status?.toUpperCase() || 'No Status'}
        </Badge>
      );
    },
  }),

  columnHelper.accessor('flagtype.name', {
    header: () => <div className="whitespace-nowrap min-w-[100px]">Flag Type</div>,
    cell: ({ row: { original } }) => {
      return (
        <div className="whitespace-nowrap min-w-[100px] !px-4 text-[13px]">
          {original.flagtype?.name || ''}
        </div>
      );
    },
  }),

  columnHelper.accessor('assigned_user.userName', {
    header: () => <div className="whitespace-nowrap min-w-[120px]">Assigned User</div>,
    cell: ({ row: { original } }) => {
      return (
        <div className="whitespace-nowrap min-w-[120px] !px-4 text-[13px]">
          {original.assigned_user?.userName || ''}
        </div>
      );
    },
  }),
];


export const getCorpOrdersColumns = ({ linkBasePath, findSKULevel, findUpcomingStock, findSKUStatus, columnHelper }) => [
  columnHelper.accessor('name', {
    header: () => 'Order ID',
    cell: ({ row: { original } }) => {
      return <div className="text-[13px] min-w-max !px-4">
        <Link to={`${linkBasePath}/${original['_id']}`} className="text-body font-weight-bold">{original.name}</Link>
      </div>;
    },
  }),

  columnHelper.accessor('orderCreated', {
    header: () => 'Created At',
    cell: ({ row: { original } }) => {
      return (
        <div className="whitespace-nowrap min-w-[100px] text-[13px]">
          {original.orderCreated ? new Date(original.orderCreated).toDateString() : ""}
        </div>
      );
    },
  }),

  columnHelper.accessor('deadline', {
    header: () => 'Due Date',
    cell: ({ row: { original } }) => {
      return (
        <div className="whitespace-nowrap min-w-[100px] text-[13px]">
          {original.deadline ? new Date(original.deadline).toDateString() : ""}
        </div>
      );
    },
  }),

  columnHelper.accessor('shipping_address', {
    header: () => <div className="whitespace-nowrap min-w-max">Region</div>,
    cell: ({ row: { original } }) => {
      const { country } = original.shipping_address || {};
      return (
        <div className="whitespace-nowrap min-w-max !px-4 text-[13px]">
          {country || ''}
        </div>
      );
    },
  }),

  columnHelper.accessor('pick_status', {
    header: () => (<div className='min-w-max'>Pick Status</div>),
    cell: ({ row: { original } }) => (
      <Badge
        className={`!px-4 min-w-max text-[12px] badge-soft-${pickToBadge(original.pick_status)}`}
        color={pickToBadge(original.pick_status)}
      >
        {pickToName(original.pick_status)}
      </Badge>
    ),
  }),

  columnHelper.accessor('fulfillment_status', {
    header: () => 'Fulfillment',
    cell: ({ row: { original } }) => {
      return (
        <Badge
          className={`!px-4 text-[12px] badge-soft-${fulfillmentToBadge(
            original.fulfillment_status
          )}`}
        >
          {original.fulfillment_status
            ? original.fulfillment_status.replace('_', ' ').toUpperCase()
            : 'UN-FULFILLED'}
        </Badge>
      );
    },
  }),

  columnHelper.accessor('priority', {
    header: () => 'Priority',
    cell: ({ row: { original } }) => {
      return (
        <Badge
          className={`!px-4 text-[12px] badge-soft-${priorityColor(original.priority)}`}
        >
          {original.priority?.toUpperCase() || 'NONE'}
        </Badge>
      );
    },
  }),

  columnHelper.accessor('personalisation', {
    header: () => 'Contains Personalisation',
    cell: ({ row: { original } }) => {
      const hasPersonalisation = original.line_items?.some(item => 
        item.properties?.some(prop => prop.name === 'Personalisation')
      );
      return (
        <div className="text-[13px] !px-4">
          {hasPersonalisation ? 'Yes' : 'No'}
        </div>
      );
    },
  }),

  columnHelper.accessor('skus', {
    header: () => 'SKUs',
    cell: ({ row: { original }, row }) => {
      if (original.lineSum?.length === 1) {
        return (
          <div className="min-w-[120px]">
            <Badge
              key={original?.lineSum?.[0]?.sku}
              className="font-size-12 badge-soft-primary"
              variant="primary"
            >
              <SKUToolTip sku={original.lineSum[0].sku} findSKULevel={findSKULevel} findUpcomingStock={findUpcomingStock} findSKUStatus={findSKUStatus} />
            </Badge>
          </div>
        );
      }
      let firstSKU = original?.lineSum[0]?.sku || '';
      let firstQuantity = original?.lineSum[0]?.quantity || '';
      
      return (
        <div className="flex items-center min-w-[120px]">
          <Badge
            key={firstSKU}
            className="badge-soft-primary font-size-12"
            variant="primary"
          >
            <SKUToolTip sku={firstSKU} findSKULevel={findSKULevel} findUpcomingStock={findUpcomingStock} findSKUStatus={findSKUStatus} /> x {firstQuantity}
          </Badge>
          <button
            onClick={() => row.toggleExpanded()}
            aria-label="expand row"
            className={`ml-2 w-4 h-4 flex items-center justify-center rounded-full text-white ${
              row.getIsExpanded() ? 'bg-blue-500' : 'bg-gray-500'
            }`}
          >
            {row.getIsExpanded() ? '-' : '+'}
          </button>
        </div>
      );
    },
  }),

  columnHelper.accessor('assigned_user.userName', {
    header: () => <div className="whitespace-nowrap min-w-[120px]">Assigned User</div>,
    cell: ({ row: { original } }) => {
      return (
        <div className="whitespace-nowrap min-w-[120px] !px-4 text-[13px]">
          {original.assigned_user?.userName || ''}
        </div>
      );
    },
  }),
]; 
